/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.19.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { FileUploadHeader } from '../../api/headers';
import { API_HOSTNAME, WIZARD, WIZARD_ACTION } from '../../modules/app/Api';
import { HttpStatus } from '../../modules/app/appConstants';
import { appHistoryPush } from '../../modules/app/appHistory';
import EFormValidation from '../../modules/form/const/formValidation';
import { LOAD_INTERNAL_TRANSLATIONS_SUCCESS } from '../../modules/internationalization/constants';
import { ELocaleKeys } from '../../modules/internationalization/intConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardMultiSearch,
} from '../../modules/internationalization/intSelectors';
import { ICommonTranslation, IWizardMultiSearchTranslation } from '../../modules/internationalization/types';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { modalOpen } from '../../modules/modal/modalActions';
import { IModalRenderProps } from '../../modules/modal/types/modalTypes';
import {
    wizardNextAction,
    wizardStorageSaveStepAction,
    wizardStorageUpdateStepAction,
} from '../../modules/wizard/wizardActions';
import {
    EWizardName,
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '../../modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '../../modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '../../modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '../../modules/wizard/wizardTypes';
import { RoutePath } from '../../routes/constants/routesConstants';
import { requestSaga } from '../../sagas/requestSaga';
import { formQueryParamsString } from '../../sagas/utils/formQueryParamsString';
import { stringFormat, stringFormatParams } from '../../utils/stringFormat';
import {
    wizardMultiSearchAttachFileFailed,
    wizardMultiSearchAttachFileSuccessful,
    wizardMultiSearchGetSubscriberListFailureAction,
    wizardMultiSearchGetSubscriberListSuccessAction,
    wizardMultiSearchSetParamsAction,
    wizardMultiSearchSetStepValidAction,
} from './actions';
import {
    EWizardMultiSearchSteps,
    WIZARD_MULTI_SEARCH_ATTACH_FILE,
    WIZARD_MULTI_SEARCH_ATTACH_FILE_FAILED,
    WIZARD_MULTI_SEARCH_ATTACH_FILE_SUCCESSFUL,
    WIZARD_MULTI_SEARCH_GET_SUBSCRIBER_LIST,
    WIZARD_MULTI_SEARCH_HANDLE_NEXT,
    WIZARD_MULTI_SEARCH_SET_PARAMS,
} from './constant';
import {
    makeSelectWizardMultiSearchFileName,
    makeSelectWizardMultiSearchStepConfirm,
    makeSelectWizardMultiSearchSubscriberList,
} from './selectors';
import {
    IWizardMultiSearchGetSubscriberListAction,
    IWizardMultiSearchStepConfirm,
    IWizardMultiSearchStepSelection,
    TWizardMultiSearchAttachFileAction,
} from './types';

/*
* -------------------------------------------
*           INITIALIZING
* -------------------------------------------
*/

export function* wizardMultiSearchMetaSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardMultiSearchSteps.Selection:
            const stepSelection = action.payload.step as unknown as IWizardMultiSearchStepSelection;
            yield put(wizardMultiSearchSetParamsAction({
                templateUrl: stepSelection.templateUrl || '',
                maxRows: stepSelection.maxRows || 0,
            }));
            break;
        case EWizardMultiSearchSteps.Confirm:
            const stepConfirm = action.payload.step as unknown as IWizardMultiSearchStepConfirm;
            yield put(wizardMultiSearchSetParamsAction({
                foundMsisdnsCount: stepConfirm.foundMsisdnsCount || 0,
                notFoundMsisdnsCount: stepConfirm.notFoundMsisdnsCount || 0,
            }));
            break;
    }

    yield call(wizardMultiSearchLoadDataFromStorageSaga);
}

/*
* -------------------------------------------
*       SEND DATA ON NEXT STEP
* -------------------------------------------
*/

export function* wizardMultiSearchHandleNextSaga(): SagaIterator {
    yield put(wizardNextAction({}));
}

export function* wizardMultiSearchHandleNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardMultiSearchSteps.Selection:
            const step = action.payload.nextStep?.step as unknown as IWizardMultiSearchStepConfirm;
            yield put(wizardMultiSearchSetParamsAction({
                foundMsisdnsCount: step.foundMsisdnsCount || 0,
                notFoundMsisdnsCount: step.notFoundMsisdnsCount || 0,
            }));
            break;
        case EWizardMultiSearchSteps.Confirm:
            appHistoryPush(RoutePath.subscriberMobile);
            break;
    }
}

/*
* -------------------------------------------
*       VALIDATION
* -------------------------------------------
*/

export function* wizardMultiSearchValidateSaga(): SagaIterator {
    let isStepValid = true;
    let validateError = '';

    const stepName = yield select(makeSelectWizardStepName());
    const {
        validateErrors,
    }: IWizardMultiSearchTranslation = yield select(makeSelectTranslationWizardMultiSearch());

    switch (stepName) {
        case EWizardMultiSearchSteps.Selection:
            const fileName = yield select(makeSelectWizardMultiSearchFileName());

            if (!fileName) {
                isStepValid = false;
                validateError = validateErrors.file;
            }
            break;
        case EWizardMultiSearchSteps.Confirm:
            const {
                foundMsisdnsCount,
            }: IWizardMultiSearchStepConfirm = yield select(makeSelectWizardMultiSearchStepConfirm());

            if (!foundMsisdnsCount) {
                isStepValid = false;
                validateError = validateErrors.foundMsisdnsCount;
            }
            break;
    }

    yield put(wizardMultiSearchSetStepValidAction({
        isStepValid,
        validateError,
    }));
}


/*
* -------------------------------------------
*       STORAGE
* -------------------------------------------
*/

export function* wizardMultiSearchLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    /**
     * Если обновили страницу обновим всю информацию, которая у нас есть
     *
     * @author Yakov Gushchin
     * @version 1.19.0
     * @since 1.19.0
     */
    if (localStorageParams) {
        yield put(wizardMultiSearchSetParamsAction({
            fileName: localStorageParams.fileName,
        }));
    }
}

export function* wizardImportPersonalDataUpdateStorageSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    const fileName = yield select(makeSelectWizardMultiSearchFileName());

    switch (stepName) {
        case EWizardMultiSearchSteps.Selection:
            yield put(wizardStorageUpdateStepAction({
                fileName,
            }));
    }

}

export function* wizardMultiSearchStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

/*
* -------------------------------------------
*       ATTACH FILE
* -------------------------------------------
*/

export function* wizardMultiSearchAttachFileSaga({ payload }: TWizardMultiSearchAttachFileAction): SagaIterator {
    try {
        const options = { headers: FileUploadHeader };
        const url = API_HOSTNAME + stringFormat(WIZARD, EWizardName.MULTI_SEARCH)
            + stringFormat(WIZARD_ACTION, 'upload-msisdn-list');

        const data = new FormData();
        data.append('file', payload.file);

        const {
            foundMsisdnsCount,
            notFoundMsisdnsCount,
        } = yield call(requestSaga, url, 'POST', data, options);

        yield put(wizardMultiSearchAttachFileSuccessful({
            fileName: payload.file.name,
        }));
        yield put(wizardMultiSearchSetParamsAction({
            foundMsisdnsCount,
            notFoundMsisdnsCount,
        }));
    } catch (e: any) {
        const { form }: ICommonTranslation = yield select(makeSelectTranslationCommon());
        const errorCode: EFormValidation = e?.response?.error?.code ?? EFormValidation.fileNotFound;

        let error = form.validation[errorCode] || form.validation[EFormValidation.fileNotFound];
        if (errorCode === EFormValidation.fileFormatIncorrect) {
            error = stringFormatParams(error, { [ELocaleKeys.format]: 'xls, xlsx, csv' });
        }


        yield put(wizardMultiSearchAttachFileFailed(error));
    }
}

/*
* -------------------------------------------
*       SUBSCRIBER LIST
* -------------------------------------------
*/

export function* wizardMultiSearchGetSubscriberListSaga(
    { payload }: IWizardMultiSearchGetSubscriberListAction,
): SagaIterator {
    try {
        const subscriberList = yield select(makeSelectWizardMultiSearchSubscriberList(payload.isFound));
        const queryString = formQueryParamsString({
            from: payload.start ? 0 : subscriberList.length,
            size: 10,
        });

        const url = API_HOSTNAME + stringFormat(WIZARD, EWizardName.MULTI_SEARCH)
            + stringFormat(WIZARD_ACTION, payload.isFound ? 'list-found' : 'list-not-found')
            + queryString;

        const { msisdns } = yield call(requestSaga, url, 'GET');

        yield put(wizardMultiSearchGetSubscriberListSuccessAction({
            hasMore: msisdns?.length === 10,
            msisdn: payload.start ? (msisdns || []) : [...subscriberList, ...(msisdns || [])],
            isFound: payload.isFound,
        }));
    } catch (error: any) {
        yield put(wizardMultiSearchGetSubscriberListFailureAction({
            error,
            isFound: payload.isFound,
        }));
    }
}

/*
* -------------------------------------------
*              Failure Handler
* -------------------------------------------
*/

export function* wizardMultiSearchFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const { error }: IWizardMultiSearchTranslation = yield select(makeSelectTranslationWizardMultiSearch());
        title = error.codes[errorCode] || translationCommon.error.codes[errorCode] || error.defaultMessage;
    }

    yield put(modalOpen({
        render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
            onCloseModal: props.onCloseModal,
            title,
            translation: translationCommon.modal,
        }),
    }));
}

/*
* -------------------------------------------
*       Wizard Sagas
* -------------------------------------------
*/

export function* wizardMultiSearchSagas() {
    yield all([
        takeEvery(WIZARD_START_SUCCESS, wizardMultiSearchStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardMultiSearchMetaSaga),
        takeEvery(WIZARD_MULTI_SEARCH_HANDLE_NEXT, wizardMultiSearchHandleNextSaga),
        takeEvery([
            WIZARD_MULTI_SEARCH_SET_PARAMS,
            LOAD_INTERNAL_TRANSLATIONS_SUCCESS,
            WIZARD_MULTI_SEARCH_ATTACH_FILE,
            WIZARD_MULTI_SEARCH_ATTACH_FILE_FAILED,
        ], wizardMultiSearchValidateSaga),

        takeEvery(WIZARD_NEXT_SUCCESS, wizardMultiSearchHandleNextSuccessSaga),

        takeEvery([
            WIZARD_NEXT_SUCCESS,
            WIZARD_BACK_SUCCESS,
        ], wizardMultiSearchLoadDataFromStorageSaga),
        takeEvery([
            WIZARD_MULTI_SEARCH_ATTACH_FILE_SUCCESSFUL,
            WIZARD_MULTI_SEARCH_ATTACH_FILE_FAILED,
        ], wizardImportPersonalDataUpdateStorageSaga),

        takeEvery(WIZARD_MULTI_SEARCH_ATTACH_FILE, wizardMultiSearchAttachFileSaga),

        takeEvery(WIZARD_MULTI_SEARCH_GET_SUBSCRIBER_LIST, wizardMultiSearchGetSubscriberListSaga),

        takeEvery([
            WIZARD_BACK_FAILED,
            WIZARD_NEXT_FAILED,
            WIZARD_RESTORE_FAILED,
            WIZARD_START_FAILED,
        ], wizardMultiSearchFailedSaga),
    ]);
}
